import React, { useState, useEffect, useCallback } from 'react';
import { useError } from '../../contexts/ErrorContext';
import { dealershipService, DealershipInfo, DealershipStats, CreateDealershipRequest, AvailableCar } from '../../services/dealershipService';
import { User } from '../../types';
import '../../styles/components/dealership.scss';

interface DealershipProps {
  user: User;
  onUserUpdate: (updates: Partial<User>) => void;
  onUpdate: () => void;
}

const Dealership: React.FC<DealershipProps> = ({ user, onUserUpdate, onUpdate }) => {
  const { showError, showSuccess } = useError();
  
  const [myDealership, setMyDealership] = useState<DealershipInfo | null>(null);
  const [dealershipStats, setDealershipStats] = useState<DealershipStats | null>(null);
  const [localDealerships, setLocalDealerships] = useState<DealershipInfo[]>([]);
  const [availableCars, setAvailableCars] = useState<AvailableCar[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'my-dealership' | 'browse' | 'create'>('my-dealership');
  
  // New state for individual dealership view
  const [selectedDealership, setSelectedDealership] = useState<DealershipInfo | null>(null);
  const [dealershipCars, setDealershipCars] = useState<AvailableCar[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'dealership'>('list');
  
  // Form states
  const [createForm, setCreateForm] = useState<CreateDealershipRequest>({
    name: '',
    description: '',
    location: 'manhattan'
  });
  const [restockForm, setRestockForm] = useState({
    carId: 0,
    quantity: 1,
    purchasePrice: 0
  });
  const [purchaseForm, setPurchaseForm] = useState({
    carId: 0,
    quantity: 1
  });

  const loadDealershipData = useCallback(async () => {
    setLoading(true);
    try {
      // Load user's dealership
      const myDealershipResult = await dealershipService.getMyDealership();
      if (myDealershipResult.success && myDealershipResult.hasDealership) {
        setMyDealership(myDealershipResult.dealership!);
        setDealershipStats(myDealershipResult.stats!);
      }

      // Load local dealerships
      const localResult = await dealershipService.getDealershipsInLocation(user.location);
      if (localResult.success) {
        setLocalDealerships(localResult.dealerships);
      }

      // Load available cars for restocking
      const carsResult = await dealershipService.getAvailableCarsForRestock();
      if (carsResult.success) {
        setAvailableCars(carsResult.cars);
      }
    } catch (error: any) {
      showError('Failed to load dealership data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [user.location, showError]);

  useEffect(() => {
    loadDealershipData();
  }, [loadDealershipData]);

  const handleCreateDealership = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.name.trim() || !createForm.description.trim()) {
      showError('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const result = await dealershipService.createDealership(createForm);
      if (result.success) {
        showSuccess('Dealership created successfully!');
        setCreateForm({ name: '', description: '', location: 'manhattan' });
        setActiveTab('my-dealership');
        await loadDealershipData();
      } else {
        showError(result.message);
      }
    } catch (error: any) {
      showError('Failed to create dealership: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRestockInventory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!restockForm.carId || restockForm.quantity <= 0 || restockForm.purchasePrice <= 0) {
      showError('Please fill in all fields correctly');
      return;
    }

    if (!myDealership) return;

    setLoading(true);
    try {
      const result = await dealershipService.restockInventory(myDealership.id, restockForm);
      if (result.success) {
        showSuccess('Inventory restocked successfully!');
        setRestockForm({ carId: 0, quantity: 1, purchasePrice: 0 });
        await loadDealershipData();
      } else {
        showError(result.message);
      }
    } catch (error: any) {
      showError('Failed to restock inventory: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseCar = async (dealershipId: string, e: React.FormEvent) => {
    e.preventDefault();
    if (!purchaseForm.carId || purchaseForm.quantity <= 0) {
      showError('Please select a car and quantity');
      return;
    }

    setLoading(true);
    try {
      const result = await dealershipService.purchaseCarFromDealership(dealershipId, purchaseForm.carId, purchaseForm.quantity);
      if (result.success) {
        showSuccess(`Successfully purchased ${result.result?.carsAdded} car(s)!`);
        setPurchaseForm({ carId: 0, quantity: 1 });
        
        // Update user money
        if (result.result?.newBalance !== undefined) {
          onUserUpdate({ money: result.result.newBalance });
        }
        
        await loadDealershipData();
        onUpdate(); // Refresh user data from server
      } else {
        showError(result.message);
      }
    } catch (error: any) {
      showError('Failed to purchase car: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeDealership = async () => {
    if (!myDealership) return;

    setLoading(true);
    try {
      const result = await dealershipService.upgradeDealership(myDealership.id);
      if (result.success) {
        showSuccess('Dealership upgraded successfully!');
        await loadDealershipData();
      } else {
        showError(result.message);
      }
    } catch (error: any) {
      showError('Failed to upgrade dealership: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDealership = async (dealership: DealershipInfo) => {
    setSelectedDealership(dealership);
    setViewMode('dealership');
    
    // Load available cars for this dealership
    try {
      const carsResult = await dealershipService.getAvailableCarsForPurchase(dealership.id);
      if (carsResult.success) {
        setDealershipCars(carsResult.cars);
      }
    } catch (error: any) {
      showError('Failed to load dealership cars: ' + error.message);
    }
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedDealership(null);
    setDealershipCars([]);
  };

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getLocationName = (location: string) => {
    const locations: { [key: string]: string } = {
      'manhattan': 'Manhattan',
      'brooklyn': 'Brooklyn',
      'queens': 'Queens',
      'bronx': 'The Bronx',
      'staten-island': 'Staten Island'
    };
    return locations[location] || location;
  };

  if (loading) {
    return <div className="dealership-loading">Loading dealership data...</div>;
  }

  // Individual dealership view
  if (viewMode === 'dealership' && selectedDealership) {
    return (
      <div className="dealership-container">
        <div className="dealership-header">
          <button onClick={handleBackToList} className="back-btn">← Back to Dealerships</button>
          <h2>🏪 {selectedDealership.name}</h2>
          <p>{selectedDealership.description}</p>
          <div className="dealership-info">
            <span>📍 {getLocationName(selectedDealership.location)}</span>
            <span>👤 Owner: {selectedDealership.owner.username}</span>
            <span>⭐ Reputation: {selectedDealership.reputation}/100</span>
            <span>📈 Level: {selectedDealership.level}</span>
          </div>
        </div>

        <div className="dealership-cars-section">
          <h3>Available Cars for Purchase</h3>
          <p className="pricing-info">All cars are priced at 20% below market value!</p>
          
          {dealershipCars.length === 0 ? (
            <p>No cars available for purchase at this dealership.</p>
          ) : (
            <div className="cars-grid">
              {dealershipCars.map(car => (
                <div key={car.id} className="car-card">
                  <img src={car.image} alt={car.name} className="car-image" />
                  <div className="car-details">
                    <h4>{car.name}</h4>
                    <p className="car-description">{car.description}</p>
                    <div className="car-stats">
                      <span>🚗 Speed: {car.speed}x</span>
                      <span>⭐ Rank Required: {car.rankRequired}</span>
                    </div>
                    <div className="car-pricing">
                      <p className="base-price">Base Value: {formatMoney(car.value)}</p>
                      <p className="purchase-price">Purchase Price: {formatMoney(car.purchasePrice || 0)}</p>
                      <p className="savings">You Save: {formatMoney(car.value - (car.purchasePrice || 0))}</p>
                    </div>
                    
                    {user.rankId >= (car.rankRequired || 1) ? (
                      <form onSubmit={(e) => handlePurchaseCar(selectedDealership.id, e)} className="purchase-form">
                        <div className="quantity-selector">
                          <label>Quantity:</label>
                          <input
                            type="number"
                            value={purchaseForm.carId === car.id ? purchaseForm.quantity : 1}
                            onChange={(e) => setPurchaseForm({
                              carId: car.id,
                              quantity: parseInt(e.target.value)
                            })}
                            min="1"
                            max="10"
                            required
                          />
                        </div>
                        <button 
                          type="submit" 
                          disabled={loading}
                          className="purchase-btn"
                        >
                          Buy for {formatMoney((car.purchasePrice || 0) * (purchaseForm.carId === car.id ? purchaseForm.quantity : 1))}
                        </button>
                      </form>
                    ) : (
                      <div className="rank-requirement">
                        <p>❌ Requires Rank {car.rankRequired}</p>
                        <p>Your Rank: {user.rankId}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main dealership list view
  return (
    <div className="dealership-container">
      <div className="dealership-header">
        <h2>🚗 Car Dealerships</h2>
        <p>Buy, sell, and manage cars in the automotive business</p>
      </div>

      <div className="dealership-tabs">
        <button 
          className={activeTab === 'my-dealership' ? 'active' : ''}
          onClick={() => setActiveTab('my-dealership')}
        >
          My Dealership
        </button>
        <button 
          className={activeTab === 'browse' ? 'active' : ''}
          onClick={() => setActiveTab('browse')}
        >
          Browse Dealerships
        </button>
        {!myDealership && (
          <button 
            className={activeTab === 'create' ? 'active' : ''}
            onClick={() => setActiveTab('create')}
          >
            Create Dealership
          </button>
        )}
      </div>

      <div className="dealership-content">
        {/* My Dealership Tab */}
        {activeTab === 'my-dealership' && (
          <div className="my-dealership">
            {myDealership ? (
              <div className="dealership-info">
                <div className="dealership-header-info">
                  <h3>{myDealership.name}</h3>
                  <p className="description">{myDealership.description}</p>
                  <div className="dealership-stats">
                    <span>📍 {getLocationName(myDealership.location)}</span>
                    <span>💰 ${myDealership.money.toLocaleString()}</span>
                    <span>⭐ {myDealership.reputation}/100</span>
                    <span>📈 Level {myDealership.level}</span>
                  </div>
                </div>

                {dealershipStats && (
                  <div className="dealership-stats-grid">
                    <div className="stat-card">
                      <h4>Inventory</h4>
                      <p>{dealershipStats.currentInventory}/{dealershipStats.maxInventory}</p>
                    </div>
                    <div className="stat-card">
                      <h4>Total Value</h4>
                      <p>{formatMoney(dealershipStats.totalInventoryValue)}</p>
                    </div>
                    <div className="stat-card">
                      <h4>Potential Profit</h4>
                      <p>{formatMoney(dealershipStats.totalPotentialProfit)}</p>
                    </div>
                    <div className="stat-card">
                      <h4>Cars Sold</h4>
                      <p>{dealershipStats.totalCarsSold}</p>
                    </div>
                  </div>
                )}

                <div className="dealership-actions">
                  <button 
                    onClick={handleUpgradeDealership}
                    disabled={loading || (dealershipStats?.level || 1) >= 10}
                    className="upgrade-btn"
                  >
                    Upgrade Dealership (${((dealershipStats?.level || 1) * 50000).toLocaleString()})
                  </button>
                </div>

                <div className="inventory-section">
                  <h4>Inventory Management</h4>
                  <form onSubmit={handleRestockInventory} className="restock-form">
                    <select
                      value={restockForm.carId}
                      onChange={(e) => {
                        const carId = parseInt(e.target.value);
                        const car = availableCars.find(c => c.id === carId);
                        setRestockForm({
                          ...restockForm,
                          carId,
                          purchasePrice: car?.price || 0
                        });
                      }}
                      required
                    >
                      <option value={0}>Select a car to restock</option>
                      {availableCars.map(car => (
                        <option key={car.id} value={car.id}>
                          {car.name} - ${car.price?.toLocaleString()}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      placeholder="Quantity"
                      value={restockForm.quantity}
                      onChange={(e) => setRestockForm({...restockForm, quantity: parseInt(e.target.value)})}
                      min="1"
                      max="10"
                      required
                    />
                    <input
                      type="number"
                      placeholder="Purchase Price"
                      value={restockForm.purchasePrice}
                      onChange={(e) => setRestockForm({...restockForm, purchasePrice: parseInt(e.target.value)})}
                      min="1"
                      required
                    />
                    <button type="submit" disabled={loading}>Restock</button>
                  </form>

                  <div className="inventory-list">
                    <h5>Current Inventory</h5>
                    {myDealership.inventory.length === 0 ? (
                      <p>No cars in inventory. Restock to get started!</p>
                    ) : (
                      <div className="inventory-grid">
                        {myDealership.inventory.map((item, index) => (
                          <div key={index} className="inventory-item">
                            <img src={item.carImage} alt={item.carName} />
                            <h6>{item.carName}</h6>
                            <p>Quantity: {item.quantity}</p>
                            <p>Purchase: {formatMoney(item.purchasePrice)}</p>
                            <p>Selling: {formatMoney(item.sellingPrice)}</p>
                            <p>Profit: {formatMoney(item.sellingPrice - item.purchasePrice)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-dealership">
                <h3>You don't own a dealership yet</h3>
                <p>Create your own dealership to start selling cars and making money!</p>
                <button onClick={() => setActiveTab('create')} className="create-btn">
                  Create Dealership
                </button>
              </div>
            )}
          </div>
        )}

        {/* Browse Dealerships Tab */}
        {activeTab === 'browse' && (
          <div className="browse-dealerships">
            <h3>Dealerships in {getLocationName(user.location)}</h3>
            {localDealerships.length === 0 ? (
              <p>No dealerships available in this location.</p>
            ) : (
              <div className="dealerships-grid">
                {localDealerships.map(dealership => (
                  <div key={dealership.id} className="dealership-card">
                    <div className="dealership-card-header">
                      <h4>{dealership.name}</h4>
                      <p>{dealership.description}</p>
                      <span className="owner">Owner: {dealership.owner.username}</span>
                    </div>
                    
                    <div className="dealership-card-stats">
                      <span>⭐ {dealership.reputation}/100</span>
                      <span>📈 Level {dealership.level}</span>
                      <span>🚗 {dealership.currentInventory} cars</span>
                    </div>

                    <div className="dealership-actions">
                      <button 
                        onClick={() => handleViewDealership(dealership)}
                        className="view-dealership-btn"
                      >
                        🏪 View Dealership & Buy Cars
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Create Dealership Tab */}
        {activeTab === 'create' && (
          <div className="create-dealership">
            <h3>Create Your Own Dealership</h3>
            <p>Starting a dealership costs $100,000 and gives you $50,000 in initial funds.</p>
            
            <form onSubmit={handleCreateDealership} className="create-form">
              <div className="form-group">
                <label>Dealership Name</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                  placeholder="Enter dealership name"
                  maxLength={50}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                  placeholder="Describe your dealership"
                  maxLength={500}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Location</label>
                <select
                  value={createForm.location}
                  onChange={(e) => setCreateForm({...createForm, location: e.target.value})}
                  required
                >
                  <option value="manhattan">Manhattan</option>
                  <option value="brooklyn">Brooklyn</option>
                  <option value="queens">Queens</option>
                  <option value="bronx">The Bronx</option>
                  <option value="staten-island">Staten Island</option>
                </select>
              </div>
              
              <button type="submit" disabled={loading || user.money < 100000}>
                {user.money < 100000 
                  ? `Need $${(100000 - user.money).toLocaleString()} more` 
                  : 'Create Dealership ($100,000)'
                }
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dealership;
